import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { useUsage } from "../lib/queries";

/**
 * Billing — and it says, plainly, that there is none yet.
 *
 * There is no payment provider wired to this product: no customer records, no
 * plan, no invoices. The feed's paywall admits the same thing when tapped
 * ("Checkout is not connected yet"), and this page has to agree with it.
 *
 * The alternative was a page showing "Free plan · £0" and an empty invoice
 * table, which is worse than nothing — it is a working billing screen that
 * happens to be lying, and a founder reading it concludes her card is on file
 * somewhere. An honest empty state is the whole product's posture (rule #2),
 * and it applies to our own commercials before it applies to her rooms.
 *
 * What replaces this when billing lands: plan, renewal date, payment method,
 * and invoices from the provider — none of which can be faked convincingly,
 * which is rather the point.
 *
 * What *is* real, and lives here because this is the page a founder opens
 * when she wants to know what something costs: the third-party spend her
 * workspace has actually caused this month. It is not a bill and does not
 * pretend to be one — it is the meter reading.
 */
export const Route = createFileRoute("/settings/billing")({
  component: Billing,
});

function Billing() {
  return (
    <div className="setwrap">
      <header className="sethead">
        <Link to="/settings" className="setback">
          <ArrowLeft size={16} aria-hidden />
          Settings
        </Link>
        <h1 className="settitle">Subscription</h1>
      </header>

      <section className="setnote">
        <h2>Not open yet</h2>
        <p>
          Omina has no billing connected — no plan, no card, no invoices.
          Everything you have used so far is the free tier, and there is
          currently no way to leave it.
        </p>
        <p>
          The feed says the same thing when you tap “Unlock all rooms”. When
          checkout exists, your plan, renewal, payment method and invoices will
          all live on this page.
        </p>

        <h2 className="setnotesub">Invoices</h2>
        <p>
          None — there is nothing to bill for yet. Receipts will appear here as
          they are issued, rather than on a page of their own: an invoice is a
          view of a plan, and a founder with no plan has no invoices to look at.
        </p>
      </section>

      <UsageSection />
    </div>
  );
}

/** Micro-dollars as money. Kept next to its only caller — the API is the one
 *  place that decides what a workspace spent; this only renders it. */
function money(micro: number): string {
  return `$${(micro / 1_000_000).toFixed(2)}`;
}

const PROVIDER_LABELS: Record<string, string> = {
  exa: "Web search",
  anthropic: "Language model",
  gemini: "Language model (Google)",
  apify: "Event listings",
};

/**
 * The meter reading.
 *
 * Deliberately not framed as an amount she owes: nothing bills her for this.
 * It is here so the number exists somewhere she can see it, and so a provider
 * running out of credits has a place to say so — otherwise the radar simply
 * goes quiet and the only available conclusion is that Omina is broken.
 */
function UsageSection() {
  const usage = useUsage();

  if (usage.isPending || !usage.data) return null;
  const { monthMicro, byProvider, outOfCredits, budgetMicro } = usage.data;

  return (
    <section className="setnote">
      <h2>What this month has cost to run</h2>

      {outOfCredits.length > 0 && (
        <p className="hint">
          {outOfCredits.length === 1 ? "A provider has" : "Providers have"} run
          out of credits, so anything that depends on{" "}
          {outOfCredits.length === 1 ? "it" : "them"} is paused:{" "}
          {outOfCredits.map((p) => PROVIDER_LABELS[p] ?? p).join(", ")}. This is
          an account balance, not a fault — topping it up restores the feature.
        </p>
      )}

      {monthMicro === 0 && byProvider.length === 0 ? (
        <p>
          Nothing yet this month. Searches and drafts are what cost money here,
          and you have not run any since the 1st.
        </p>
      ) : (
        <>
          <p>
            <strong>{money(monthMicro)}</strong> in third-party calls — the
            searches behind your radar, the model that writes your drafts, and
            the listings feeding your calendar.
            {budgetMicro !== null && (
              <> Your workspace is capped at {money(budgetMicro)} a month.</>
            )}
          </p>
          <ul>
            {byProvider.map((row) => (
              <li key={row.provider}>
                {PROVIDER_LABELS[row.provider] ?? row.provider} ·{" "}
                {money(row.costMicro)} over {row.requests}{" "}
                {row.requests === 1 ? "call" : "calls"}
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}
